import React, { useEffect, useState, useCallback } from "react";
import { FileDown, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import OfficerShell from "./OfficerShell";
import TehsilOfficeDashboard from "./TehsilOfficeDashboard";
import { TEH_LABEL } from "./SdoDashboard";
import {
  MARKING_SECTIONS, MARK_DOCS, exportExcel,
  isFinalEligible, isFinalIneligible, applicant, lbName,
} from "./RdcDashboard";

const INTERVIEW_SI = 4; // "५. मुलाखतीद्वारे"

// ---- मुलाखतीचे गुण भरण्याचा form (फक्त विभाग ५) ----
function AroInterviewForm({ appId, onDone }) {
  const [app, setApp] = useState(null);
  const [scores, setScores] = useState({});
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.officerDetail(appId).then((a) => {
      if (!alive) return;
      setApp(a);
      setScores((a.marking || {}).scores || {});
    }).catch(() => {});
    return () => { alive = false; };
  }, [appId]);

  if (!app) return <div className="rounded-2xl bg-white p-10 text-center text-slate-400">लोड होत आहे...</div>;

  const sec = MARKING_SECTIONS[INTERVIEW_SI];
  const d = app.data || {};
  const setScore = (key, max, val) => {
    const n = val === "" ? "" : Math.max(0, Math.min(max, Number(val)));
    setScores((s) => ({ ...s, [key]: n }));
  };
  const subtotal = sec.items.reduce((a, _, ci) => a + (Number(scores[`s${INTERVIEW_SI}_${ci}`]) || 0), 0);
  const secMax = sec.items.reduce((a, [, m]) => a + m, 0);
  const allFilled = sec.items.every((_, ci) => {
    const v = scores[`s${INTERVIEW_SI}_${ci}`];
    return v !== undefined && v !== "";
  });

  const save = async () => {
    setBusy(true);
    try {
      const s4 = {};
      sec.items.forEach((_, ci) => {
        const k = `s${INTERVIEW_SI}_${ci}`;
        if (scores[k] !== undefined && scores[k] !== "") s4[k] = scores[k];
      });
      await api.aroInterviewMarking(appId, { scores: s4, remark });
      toast.success(`मुलाखतीचे गुण जतन झाले — ${subtotal}/${secMax}. (RDC गुणांकनात समाविष्ट)`);
      onDone && onDone();
    } catch (e) { toast.error("त्रुटी: " + (e.message || "")); }
    setBusy(false);
  };

  return (
    <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5" data-testid="aro-interview-form">
      <p className="text-center text-[15px] font-bold text-[#1b5e3f]">मुलाखतीचे गुण — {sec.title}</p>
      <p className="mb-4 text-center text-[12.5px] text-slate-500">
        अर्ज क्र. {app.applicationNumber} · {d.nameMr || app.applicantName} · {d.village || d.city || ""} · {TEH_LABEL[app.tehsil] || app.tehsil}
      </p>
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-left text-[11.5px] text-slate-400">
            <th className="p-1">निकष</th>
            <th className="w-16 p-1 text-center">कमाल</th>
            <th className="w-24 p-1 text-center">प्राप्त गुण</th>
          </tr>
        </thead>
        <tbody>
          {sec.items.map(([label, max], ci) => (
            <tr key={ci} className="border-b border-slate-100">
              <td className="p-2">{ci + 1}. {label}</td>
              <td className="w-16 p-2 text-center text-slate-400">/ {max}</td>
              <td className="w-24 p-2">
                <input type="number" min={0} max={max} value={scores[`s${INTERVIEW_SI}_${ci}`] ?? ""}
                  onChange={(e) => setScore(`s${INTERVIEW_SI}_${ci}`, max, e.target.value)}
                  data-testid={`interview-mark-c${ci}`}
                  className="w-full rounded border border-slate-300 px-2 py-1 text-center text-[13px] outline-none focus:border-[#1b5e3f]" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="my-3 flex items-center justify-between rounded-xl bg-[#f3efe2] px-4 py-3">
        <span className="text-[15px] font-bold text-[#1b3d2a]">मुलाखत एकूण गुण</span>
        <span className="text-[20px] font-bold text-[#1b5e3f]" data-testid="interview-subtotal">{subtotal} / {secMax}</span>
      </div>
      <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={2} placeholder="शेरा (ऐच्छिक)"
        className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f]" />
      <button onClick={save} disabled={busy || !allFilled} data-testid="interview-save"
        className="w-full rounded-full bg-[#1b5e3f] py-3 text-[14px] font-bold text-white hover:bg-[#144d33] disabled:opacity-60">
        {allFilled ? "मुलाखतीचे गुण जतन करा" : "सर्व निकषांचे गुण भरा"}
      </button>
    </div>
  );
}

// ---- गुणांकन (मुलाखत) टॅब ----
function AroInterviewTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [search, setSearch] = useState("");
  const [tehFilter, setTehFilter] = useState("");
  const [lbFilter, setLbFilter] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.aroInterviewList().then((d) => setRows(d || [])).catch(() => {}).finally(() => { setLoading(false); setOpenId(null); });
  }, []);
  useEffect(() => { load(); }, [load]);

  if (openId) {
    return (
      <div>
        <button onClick={() => setOpenId(null)} data-testid="interview-back"
          className="mb-3 rounded-full border border-slate-300 px-4 py-1.5 text-[13px] font-semibold text-slate-600 hover:bg-slate-50">← यादीकडे परत</button>
        <AroInterviewForm appId={openId} onDone={load} />
      </div>
    );
  }

  const tehsilOpts = [...new Set(rows.map((r) => r.tehsil).filter(Boolean))];
  const lbOpts = [...new Set(rows.filter((r) => !tehFilter || r.tehsil === tehFilter).map((r) => lbName(r)).filter(Boolean))];
  const filtered = rows.filter((r) =>
    (!search || String(r.applicationNumber || "").toLowerCase().includes(search.toLowerCase())) &&
    (!tehFilter || r.tehsil === tehFilter) &&
    (!lbFilter || lbName(r) === lbFilter));

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-[#e6dcc4] bg-white p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="अर्ज क्रमांकाने शोधा"
            data-testid="interview-search"
            className="w-56 rounded-full border border-slate-200 py-2 pl-9 pr-3 text-[13.5px] outline-none focus:border-[#1b5e3f]" />
        </div>
        <select value={tehFilter} onChange={(e) => { setTehFilter(e.target.value); setLbFilter(""); }} data-testid="interview-filter-tehsil"
          className="rounded-full border border-slate-200 px-4 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f]">
          <option value="">सर्व तहसील</option>
          {tehsilOpts.map((t) => <option key={t} value={t}>{TEH_LABEL[t] || t}</option>)}
        </select>
        <select value={lbFilter} onChange={(e) => setLbFilter(e.target.value)} data-testid="interview-filter-lb"
          className="rounded-full border border-slate-200 px-4 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f]">
          <option value="">सर्व स्थानिक स्वराज्य संस्था</option>
          {lbOpts.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>

      <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5">
        <p className="mb-3 text-[15px] font-bold text-[#1b5e3f]">मुलाखतीचे गुण प्रलंबित अर्ज</p>
        {loading ? (
          <div className="py-8 text-center text-slate-400">लोड होत आहे...</div>
        ) : filtered.length === 0 ? (
          <div className="py-8 text-center text-slate-400" data-testid="interview-empty">मुलाखतीचे गुण भरण्यासाठी अर्ज नाहीत.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-500">
                  <th className="p-2.5">अ.क्र.</th>
                  <th className="p-2.5">अर्ज क्र.</th>
                  <th className="p-2.5">अर्जदाराचे नाव</th>
                  <th className="p-2.5">स्थानिक स्वराज्य संस्था</th>
                  <th className="p-2.5 text-center">पहा</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a, i) => (
                  <tr key={a.id} className="border-b border-slate-100 hover:bg-[#faf8f0]">
                    <td className="p-2.5">{i + 1}</td>
                    <td className="p-2.5 font-latin font-semibold text-[#1b3d2a]">{a.applicationNumber}</td>
                    <td className="p-2.5">{applicant(a)}</td>
                    <td className="p-2.5">{lbName(a)}</td>
                    <td className="p-2.5 text-center">
                      <button onClick={() => setOpenId(a.id)} data-testid={`interview-open-${a.id}`}
                        className="rounded-full bg-[#1b5e3f] px-4 py-1.5 text-[12.5px] font-semibold text-white hover:bg-[#144d33]">पहा</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- प्राथमिक पडताळणीमधील अंतिम निर्णय झालेले अर्ज (view-only) ----
function AroFinalTab({ tehsils }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState("पात्र");
  const [tehFilter, setTehFilter] = useState("");

  useEffect(() => {
    setLoading(true);
    api.officerList("", "", "").then((list) => {
      setRows((list || []).filter((a) => isFinalEligible(a) || isFinalIneligible(a)));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = rows.filter((a) =>
    (sub === "पात्र" ? isFinalEligible(a) : isFinalIneligible(a)) &&
    (!tehFilter || a.tehsil === tehFilter));

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {["पात्र", "अपात्र"].map((s) => (
          <button key={s} onClick={() => setSub(s)} data-testid={`aro-final-subtab-${s === "पात्र" ? "eligible" : "ineligible"}`}
            className={`rounded-full px-5 py-2 text-[13.5px] font-semibold transition-colors ${sub === s ? (s === "पात्र" ? "bg-[#5a9e3a] text-white" : "bg-red-600 text-white") : "bg-white text-slate-600 ring-1 ring-slate-300 hover:bg-slate-50"}`}>
            {s} अर्ज
          </button>
        ))}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <select value={tehFilter} onChange={(e) => setTehFilter(e.target.value)} data-testid="aro-final-filter-tehsil"
            className="rounded-full border border-slate-200 px-4 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f]">
            <option value="">सर्व तहसील</option>
            {tehsils.map((t) => <option key={t} value={t}>{TEH_LABEL[t] || t}</option>)}
          </select>
          <button onClick={() => exportExcel(filtered, sub, tehFilter ? (TEH_LABEL[tehFilter] || tehFilter) : "सर्व")}
            disabled={filtered.length === 0} data-testid="aro-final-excel"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1b5e3f] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#144d33] disabled:opacity-50">
            <FileDown className="h-4 w-4" /> Excel डाउनलोड
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5">
        <p className="mb-3 text-[15px] font-bold text-[#1b5e3f]">अंतिम निर्णय झालेले {sub} अर्ज</p>
        {loading ? (
          <div className="py-8 text-center text-slate-400">लोड होत आहे...</div>
        ) : filtered.length === 0 ? (
          <div className="py-8 text-center text-slate-400" data-testid="aro-final-empty">या प्रकारात अंतिम निर्णय झालेले अर्ज नाहीत.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-500">
                  <th className="p-2.5">अ.क्र.</th>
                  <th className="p-2.5">तहसील</th>
                  <th className="p-2.5">स्थानिक स्वराज्य संस्था</th>
                  <th className="p-2.5">अर्ज क्र.</th>
                  <th className="p-2.5">अर्जदाराचे नाव</th>
                  <th className="p-2.5">निर्णय</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a, i) => (
                  <tr key={a.id} className="border-b border-slate-100 hover:bg-[#faf8f0]">
                    <td className="p-2.5">{i + 1}</td>
                    <td className="p-2.5">{TEH_LABEL[a.tehsil] || a.tehsil}</td>
                    <td className="p-2.5">{lbName(a)}</td>
                    <td className="p-2.5 font-latin font-semibold text-[#1b3d2a]">{a.applicationNumber}</td>
                    <td className="p-2.5">{applicant(a)}</td>
                    <td className="p-2.5">
                      <span className={`rounded-full px-2.5 py-1 text-[12px] font-bold ${sub === "पात्र" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>{sub}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AroDashboard() {
  const [view, setView] = useState("dash");
  const [meta, setMeta] = useState(null);

  useEffect(() => { api.officerMeta().then(setMeta).catch(() => {}); }, []);
  const tehsils = (meta && meta.scopeTehsils) || [];

  const tabBtn = (key, label) => (
    <button onClick={() => setView(key)} data-testid={`aro-view-${key}`}
      className={`rounded-full px-5 py-2 text-[13.5px] font-semibold transition-colors ${view === key ? "bg-[#1b5e3f] text-white" : "bg-white text-slate-600 ring-1 ring-slate-300 hover:bg-slate-50"}`}>{label}</button>
  );

  return (
    <OfficerShell>
      <div className="mb-5">
        <h1 className="font-head text-[26px] font-medium text-[#1b5e3f]">सहायक अधिकारी (ARO) डॅशबोर्ड</h1>
        <p className="text-[14px] text-slate-500">क्षेत्र: संपूर्ण जिल्हा · सांगली · मुलाखतीचे गुणांकन</p>
      </div>

      <div className="mb-5 flex flex-wrap gap-2">
        {tabBtn("dash", "डॅशबोर्ड")}
        {tabBtn("final", "प्राथमिक पडताळणीमधील अंतिम निर्णय झालेले अर्ज")}
        {tabBtn("interview", "गुणांकन (मुलाखत)")}
      </div>

      {view === "dash" && <TehsilOfficeDashboard />}
      {view === "final" && <AroFinalTab tehsils={tehsils} />}
      {view === "interview" && <AroInterviewTab />}
    </OfficerShell>
  );
}
